import React, { FC, ReactNode, memo } from 'react';
import styled from '@emotion/styled';

/**
 * Shared skeleton for the three checkout steps: a title, a line under it,
 * whatever the step needs in the middle, and the actions at the bottom.
 * Slides in from the right so each step change reads as progress.
 */
const Frame = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 72px 80px 64px;
  gap: 16px;
  animation: eposStepIn 0.3s ease-out both;

  @keyframes eposStepIn {
    from {
      opacity: 0;
      transform: translate3d(24px, 0, 0);
    }
    to {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: 56px;
  font-weight: 800;
  letter-spacing: -1.2px;
  line-height: 1.05;
`;

const Subtitle = styled.p`
  margin: 0;
  max-width: 760px;
  font-size: 24px;
  line-height: 1.4;
  opacity: 0.65;
`;

/** The step's own content, centered. */
const Body = styled.div`
  width: 100%;
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const Actions = styled.div`
  width: 100%;
  margin-top: 32px;
  display: flex;
  justify-content: center;
  gap: 20px;
`;

/**
 * Folds the actions open from zero height (and shut again) when `actionsOpen`
 * is passed - same `grid-template-rows` trick as CodeInput's error, so nothing
 * is measured. The -16px cancels Frame's gap while shut; the 48px inside is
 * that gap plus Actions' usual 32px margin, so the open layout is unchanged.
 */
const ActionsFold = styled.div`
  width: 100%;
  margin-top: -16px;
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 320ms cubic-bezier(0.22, 1, 0.36, 1);

  &.is-open {
    grid-template-rows: 1fr;
  }
`;

/** The actions inside the fold: slide up and fade in once there is room. */
const FoldedActions = styled(Actions)`
  min-height: 0;
  margin-top: 0;
  padding-top: 48px;
  overflow: hidden;
  pointer-events: none;
  opacity: 0;
  transform: translate3d(0, 16px, 0);
  transition: transform 320ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease-out;

  .is-open > & {
    pointer-events: auto;
    opacity: 1;
    transform: translate3d(0, 0, 0);
    transition-delay: 60ms;
  }
`;

interface Props {
  title: string;
  subtitle?: string;
  children?: ReactNode;
  actions?: ReactNode;
  /** Pass to keep `actions` mounted and fold them in and out instead of popping them. */
  actionsOpen?: boolean;
  /** Always last, under the actions - e.g. "Powered by Baemingo". */
  footer?: ReactNode;
  testId?: string;
}

const StepFrame: FC<Props> = ({
  title,
  subtitle,
  children,
  actions,
  actionsOpen,
  footer,
  testId,
}) => (
  <Frame data-testid={testId}>
    <Title>{title}</Title>
    {!!subtitle && <Subtitle>{subtitle}</Subtitle>}
    {!!children && <Body>{children}</Body>}
    {actionsOpen !== undefined ? (
      <ActionsFold className={actionsOpen ? 'is-open' : undefined}>
        <FoldedActions aria-hidden={!actionsOpen}>{actions}</FoldedActions>
      </ActionsFold>
    ) : (
      !!actions && <Actions>{actions}</Actions>
    )}
    {footer}
  </Frame>
);

export default memo(StepFrame);
